import { useStrings } from "@1js/localization-components";
import {
  BookmarkToggleButton,
  Column,
  DisplayCategoryKey,
  Grid,
  GroupPromptsFilter,
  ProductNameKey,
  PromptCard,
  PromptCardSkeleton,
  PromptResultsAnnouncement,
  PromptTab,
  RemoveFromWorkgroupDialog,
  Row,
  SavedPromptsFilter,
  ShareToWorkgroupDialog,
  WorkgroupMemberTeachingPopover,
  WorkgroupOwnerTeachingPopover,
  type FacetDictionary,
  type Prompt,
  type PromptTabs,
} from "@1js/pl-card-components";
import {
  Button,
  Dialog,
  Toaster,
  mergeClasses,
  useId,
} from "@fluentui/react-components";
import * as React from "react";
import { useShareToast } from "../../hooks/toast/useShareToast";
import { useFlightContext } from "../../hooks/useFlightContext";
import { useSharePrompt } from "../../hooks/useSharePrompt";
import { PromptDetailModal } from "../PromptDetail/PromptDetailModal.web";
import {
  ActionsPromptLinkClipboard,
  ActionsPromptLinkClipboardBody,
} from "../PromptDetail/PromptPreview/Actions.web.string";
import type { WebPrompt } from "../data";
import type { AllPromptsProps } from "./AllPrompts.web.data";
import {
  PersonalTabTitle,
  ShowMoreButtonLabel,
  WorkTabTitle,
} from "./AllPrompts.web.string";
import { useStyles } from "./AllPrompts.web.styles";
import { Filters } from "./Filters.web";
import { FiltersSkeleton } from "./FiltersSkeleton.web";

import { useSearchParams } from "react-router-dom";
import { useSwipe } from "../../hooks/useCarousel/useSwipe";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { Carousel } from "../Carousel/Carousel.web";
import { ConditionalWrap } from "../ConditionalWrap/ConditionalWrap.web";

function serializeFilters(
  obj: Record<string, string[]>,
  searchParams: URLSearchParams
): URLSearchParams {
  searchParams.delete(DisplayCategoryKey);
  searchParams.delete(ProductNameKey);
  for (const key in obj) {
    if (obj[key].length) {
      searchParams.set(key, obj[key].join("_"));
    }
  }
  return searchParams;
}

function valueExistsInFacets(
  key: string,
  value: string,
  facets: FacetDictionary
): boolean {
  const facetValues = facets[key];
  if (facetValues === undefined) {
    return false;
  }
  return facetValues.some((facetValue) => facetValue.value === value);
}

function deserializeFilters(
  searchParams: URLSearchParams,
  facets: FacetDictionary
): Record<string, string[]> {
  const obj: Record<string, string[]> = {};

  obj[DisplayCategoryKey] =
    searchParams
      .get(DisplayCategoryKey)
      ?.split("_")
      .filter((value) =>
        valueExistsInFacets(DisplayCategoryKey, value, facets)
      ) || [];
  obj[ProductNameKey] =
    searchParams
      .get(ProductNameKey)
      ?.split("_")
      .filter((value) => valueExistsInFacets(ProductNameKey, value, facets)) ||
    [];
  return obj;
}
export const AllPrompts: React.FC<AllPromptsProps> = React.memo(
  ({
    id,
    role,
    className,
    skeletonCount,
    status,
    isShowFilters = true,
    isShowHasMore = true,
    enableCarousel = false,
    hasMore,
    hasBookmarkedPrompts,
    prompts,
    morePrompts,
    facets,
    selectedFacets,
    showBookmarkOnly,
    showGroupPromptsOnly,
    savedPromptsFilter,
    groups,
    groupPromptsFilter,
    onFilterChanged,
    onPromptClicked,
    onPromptBookmarkToggled,
    onMoreClicked,
    onSharePromptClicked,
    onCopyPromptClicked,
    onBookmarkToggled,
    onPromptsTabSelected,
    onSavedPromptsFilterChanged,
    onGroupPromptsFilterChanged,
    onDelete,
    onTryInAppBadgeAction,
    onShareToWorkgroup,
    groupPrompts,
    onRemoveFromWorkgroupClicked,
    onAnnouncementSharePromptsClicked,
    onAnnouncementExploreOtherPromptsClicked,
    groupStatus,
    groupPromptStatus,
  }) => {
    const resultsAnnouncementId = useId("prompt-cards-description");
    const isLoading = status === "loading";
    const styles = useStyles();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [activePrompt, setActivePrompt] = React.useState<WebPrompt>();
    const swipe = useSwipe();
    const promptRefs = React.useRef(
      new Map<string, React.RefObject<HTMLDivElement>>()
    );
    const localizations = useStrings({
      WorkTabTitle,
      PersonalTabTitle,
      ShowMoreButtonLabel,
      ActionsPromptLinkClipboard,
      ActionsPromptLinkClipboardBody,
    });
    const sharePrompt = useSharePrompt();

    const toasterId = useId("toaster");
    const { notifyShareCopyLinkSuccess } = useShareToast(toasterId);

    const handleCardClick = (p: Prompt) => {
      if (swipe.isSwiped) {
        return;
      }
      setActivePrompt(p as WebPrompt);
      setDialogOpen(true);
      onPromptClicked(p);

      const dialog = document.getElementById("dialog");
      if (dialog) {
        dialog.scrollTop = 0;
      }
    };

    const [lastFocusedPromptId, setLastFocusedPromptId] = React.useState<
      string | null
    >(null);

    const [focusOnNextPrompt, setFocusOnNextPrompt] = React.useState(false);

    const handleDialogClose = () => {
      setDialogOpen(false);
      if (activePrompt) {
        const promptRef = promptRefs.current.get(activePrompt.Id);
        if (promptRef && promptRef.current) {
          promptRef.current.focus();
        }
      }
    };

    function handleCardFocus() {
      if (lastFocusedPromptId) {
        // Set the flag to focus on the next prompt
        setFocusOnNextPrompt(true);
      }
    }

    const [searchParams, setSearchParams] = useSearchParams();

    function handleFilterChange(filters: Record<string, string[]>) {
      const params = serializeFilters(filters, searchParams);
      setSearchParams(params);
    }

    React.useEffect(() => {
      const filters = deserializeFilters(searchParams, facets);
      onFilterChanged(filters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, facets]);

    const handleShareViaCopyLinkClicked = React.useCallback(
      (prompt: Prompt) => {
        sharePrompt.shareUrlViaCopyLink(prompt);
        notifyShareCopyLinkSuccess(prompt);
      },
      [sharePrompt, notifyShareCopyLinkSuccess]
    );

    const handleShareViaEmailClicked = React.useCallback(
      async (prompt: Prompt) => {
        await sharePrompt.shareUrlViaEmail(prompt);
      },
      [sharePrompt]
    );

    React.useLayoutEffect(() => {
      if (focusOnNextPrompt && lastFocusedPromptId) {
        // Find the index of the last focused prompt
        const lastFocusedPromptIndex = prompts.findIndex(
          (prompt) => prompt.Id === lastFocusedPromptId
        );

        // Check if there's a next prompt in the list
        if (
          lastFocusedPromptIndex !== -1 &&
          lastFocusedPromptIndex + 1 < prompts.length
        ) {
          const nextPrompt = prompts[lastFocusedPromptIndex + 1];
          const promptRef = promptRefs.current.get(nextPrompt.Id);
          if (promptRef && promptRef.current) {
            // Focus on the next prompt
            promptRef.current.focus();
            setLastFocusedPromptId(null);
          }
        }

        // Reset the flag
        setFocusOnNextPrompt(false);
      }
    }, [prompts, lastFocusedPromptId, focusOnNextPrompt, promptRefs]);

    const { isFlightActive } = useFlightContext();

    let selectedValue: PromptTabs = "AllPrompts";
    if (showBookmarkOnly) {
      selectedValue = "SavedPrompts";
    } else if (showGroupPromptsOnly) {
      selectedValue = "GroupPrompts";
    }

    function handleTabChange(value: PromptTabs) {
      if (isFlightActive("isURLRoutingEnabled")) {
        searchParams.set("tab", value);
        setSearchParams(searchParams);
      } else {
        onPromptsTabSelected && onPromptsTabSelected(value);
      }
    }

    React.useEffect(() => {
      const tab = searchParams.get("tab") as PromptTabs;
      if (onPromptsTabSelected) {
        onPromptsTabSelected(tab || "AllPrompts");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    let promptsToDisplay = prompts;
    if (showGroupPromptsOnly) {
      promptsToDisplay = groupPrompts;
    }

    const [removeDialogConfig, setRemoveDialogConfig] = React.useState<{
      isOpen: boolean;
      prompts: Prompt[] | undefined;
      groupIds: string[] | undefined;
    }>({
      isOpen: false,
      prompts: undefined,
      groupIds: undefined,
    });

    const handleOnRemoveClose = React.useCallback(() => {
      setRemoveDialogConfig({
        isOpen: false,
        prompts: undefined,
        groupIds: undefined,
      });
    }, []);

    const handleOnRemoveFromWorkgroupClicked = React.useCallback(
      (prompts: Prompt[], groupIds: string[]) => {
        setRemoveDialogConfig({
          isOpen: true,
          prompts,
          groupIds,
        });
      },
      []
    );

    const handleOnRemoveDialogClicked = React.useCallback(() => {
      !!removeDialogConfig.prompts &&
        !!removeDialogConfig.groupIds &&
        onRemoveFromWorkgroupClicked(
          removeDialogConfig.prompts,
          removeDialogConfig.groupIds
        );
      handleOnRemoveClose();
    }, [
      handleOnRemoveClose,
      onRemoveFromWorkgroupClicked,
      removeDialogConfig.groupIds,
      removeDialogConfig.prompts,
    ]);

    const [
      workgroupOwnerTeachingPopoverTarget,
      setWorkgroupOwnerTeachingPopoverTarget,
    ] = React.useState<HTMLElement | null>(null);

    const [
      workgroupMemberTeachingPopoverTarget,
      setWorkgroupMemberTeachingPopoverTarget,
    ] = React.useState<HTMLElement | null>(null);

    const { isWorkgroupPublishingEnabled } = useFeatureEnablement();

    return (
      <>
        <WorkgroupMemberTeachingPopover
          isEnabled={
            (isFlightActive("isWorkgroupTeachingPopoverEnabled") &&
              workgroupMemberTeachingPopoverTarget &&
              !showGroupPromptsOnly &&
              !showBookmarkOnly) ||
            false
          }
          target={workgroupMemberTeachingPopoverTarget}
        />
        <WorkgroupOwnerTeachingPopover
          isEnabled={
            (isFlightActive("isWorkgroupTeachingPopoverEnabled") &&
              workgroupOwnerTeachingPopoverTarget &&
              showBookmarkOnly) ||
            false
          }
          target={workgroupOwnerTeachingPopoverTarget}
        />
        <Toaster toasterId={toasterId} pauseOnHover pauseOnWindowBlur />
        {sharePrompt.isShareToWorkgroupDialogOpen && (
          <ShareToWorkgroupDialog
            isOpen={sharePrompt.isShareToWorkgroupDialogOpen}
            groups={groups}
            prompts={sharePrompt.promptsToShareToWorkgroup}
            onClose={sharePrompt.onCloseShareToWorkgroupDialog}
            onShare={onShareToWorkgroup}
            appearance={"web"}
            isAllowMultiSelect={isFlightActive(
              "isWorkgroupPublishingMultiSelectShareEnabled"
            )}
          />
        )}
        {removeDialogConfig.isOpen && (
          <RemoveFromWorkgroupDialog
            isOpen={removeDialogConfig.isOpen}
            onClose={handleOnRemoveClose}
            onRemove={handleOnRemoveDialogClicked}
          />
        )}
        <ConditionalWrap
          condition={!enableCarousel}
          wrapIfTrue={(children) => (
            <Grid
              id={id}
              role={role}
              testid="all-prompts"
              className={mergeClasses(styles.container, className)}
            >
              {children}
            </Grid>
          )}
        >
          {isShowFilters &&
            (isFlightActive("isPromptSavingEnabled") ||
              isWorkgroupPublishingEnabled) && (
              <div className={styles.promptTabRow}>
                <PromptTab
                  loading={isLoading}
                  selectedValue={selectedValue}
                  onTabSelected={handleTabChange}
                  isShowGroupTab={isWorkgroupPublishingEnabled}
                  setGroupTabTarget={setWorkgroupMemberTeachingPopoverTarget}
                />
              </div>
            )}
          {isShowFilters && (
            <div className={styles.filtersRow}>
              {isWorkgroupPublishingEnabled &&
                showGroupPromptsOnly &&
                groups && (
                  <GroupPromptsFilter
                    appearance="web"
                    isLoading={isLoading}
                    value={groupPromptsFilter}
                    onFilterChanged={onGroupPromptsFilterChanged}
                    groups={groups}
                    isStoreStateEnabled={isFlightActive(
                      "isWorkgroupPublishingGroupStateEnabled"
                    )}
                  />
                )}
              {isFlightActive("isPromptSavingEnabled") && showBookmarkOnly && (
                <SavedPromptsFilter
                  appearance="web"
                  isLoading={isLoading}
                  value={savedPromptsFilter}
                  onFilterChanged={onSavedPromptsFilterChanged}
                />
              )}
              {isFlightActive("isBookmarksEnabled") &&
                !isFlightActive("isPromptSavingEnabled") && (
                  <BookmarkToggleButton
                    className={styles.bookmarkToggle}
                    isLoading={isLoading}
                    showBookmarkOnly={showBookmarkOnly}
                    onClicked={() => {
                      if (onBookmarkToggled) {
                        onBookmarkToggled();
                      }
                    }}
                  />
                )}
              <div className={styles.filtersContainer}>
                {!showGroupPromptsOnly &&
                  (isLoading ? (
                    <FiltersSkeleton />
                  ) : (
                    <Filters
                      facets={facets}
                      selectedFacets={selectedFacets}
                      onFilterChanged={
                        isFlightActive("isURLRoutingEnabled")
                          ? handleFilterChange
                          : onFilterChanged
                      }
                    />
                  ))}
              </div>
            </div>
          )}
          <PromptResultsAnnouncement
            id={resultsAnnouncementId}
            prompts={promptsToDisplay}
            isLoading={isLoading}
            isFailed={status === "failed"}
            showFavoritesOnly={showBookmarkOnly}
            hasBookmarkedPrompts={hasBookmarkedPrompts}
            groups={groups}
            isShowGroupPrompts={showGroupPromptsOnly || false}
            groupPrompts={groupPrompts}
            onExploreOtherPrompts={onAnnouncementExploreOtherPromptsClicked}
            onSharePrompts={onAnnouncementSharePromptsClicked}
            groupStatus={groupStatus}
            groupPromptStatus={groupPromptStatus}
          />
          {isLoading || groupPromptStatus === "loading" ? (
            <ConditionalWrap
              condition={!enableCarousel}
              wrapIfTrue={(children) => (
                <Row className={styles.cards}>{children}</Row>
              )}
            >
              {/* create skeletonCount number of PromptCardSkelton component */}
              {Array.from({ length: skeletonCount }).map((_, index) => (
                <ConditionalWrap
                  key={index}
                  condition={!enableCarousel}
                  wrapIfTrue={(children) => (
                    <Column key={index} size={3} className={styles.cardCol}>
                      {children}
                    </Column>
                  )}
                >
                  <PromptCardSkeleton
                    appearance="web"
                    isFavoriteEnabled={
                      !showGroupPromptsOnly &&
                      isFlightActive("isBookmarksEnabled")
                    }
                  />
                </ConditionalWrap>
              ))}
            </ConditionalWrap>
          ) : (
            <>
              <ConditionalWrap
                condition={!enableCarousel}
                wrapIfTrue={(children) => (
                  <Row
                    role="list"
                    aria-label="Prompt"
                    aria-describedby={resultsAnnouncementId}
                    className={styles.cards}
                    data-testid="prompt-cards"
                  >
                    {children}
                  </Row>
                )}
                wrapIfFalse={(children) => (
                  <Carousel id="prompts" setSwipedState={swipe.setSwipedState}>
                    {children}
                  </Carousel>
                )}
              >
                {promptsToDisplay.map((prompt: Prompt, idx) => {
                  if (!promptRefs.current.has(prompt.Id)) {
                    promptRefs.current.set(prompt.Id, React.createRef());
                  }
                  return (
                    <ConditionalWrap
                      key={idx}
                      condition={!enableCarousel}
                      wrapIfTrue={(children) => (
                        <Column
                          key={prompt.Id}
                          size={3}
                          className={styles.cardCol}
                        >
                          {children}
                        </Column>
                      )}
                      wrapIfFalse={(children) => (
                        <div className={styles.carousel}>{children}</div>
                      )}
                    >
                      <PromptCard
                        appearance="web"
                        key={prompt.Id}
                        prompt={prompt}
                        isFavoriteEnabled={
                          !showGroupPromptsOnly &&
                          isFlightActive("isBookmarksEnabled")
                        }
                        isPromptSavingEnabled={
                          !showGroupPromptsOnly &&
                          isFlightActive("isPromptSavingEnabled")
                        }
                        isPromptSharingEnabled={
                          !showGroupPromptsOnly &&
                          isFlightActive("isPromptSharingEnabled")
                        }
                        isPromptSharingViaEmailEnabled={
                          !showGroupPromptsOnly &&
                          isFlightActive("isPromptSharingViaEmailEnabled")
                        }
                        isPromptSharingToWorkgroupEnabled={
                          isWorkgroupPublishingEnabled &&
                          showBookmarkOnly &&
                          (isFlightActive(
                            "isWorkgroupPublishingMicrosoftPromptsEnabled"
                          ) ||
                            prompt.IsUserPrompt)
                        }
                        isPromptRemovingFromWorkgroupEnabled={
                          isWorkgroupPublishingEnabled && showGroupPromptsOnly
                        }
                        onShareToWorkgroupClicked={sharePrompt.shareToWorkgroup}
                        onRemoveFromWorkgroupClicked={
                          handleOnRemoveFromWorkgroupClicked
                        }
                        onShareViaCopyLinkClicked={
                          handleShareViaCopyLinkClicked
                        }
                        onShareViaEmailClicked={handleShareViaEmailClicked}
                        onClicked={handleCardClick}
                        onFavoriteToggled={onPromptBookmarkToggled}
                        isBadgesEnabled={true}
                        ref={promptRefs.current.get(prompt.Id)}
                        onFocus={() => setLastFocusedPromptId(prompt.Id)}
                        onDelete={onDelete}
                        aria-posinset={idx + 1}
                        aria-setsize={prompts.length}
                        shownAsPartOfGroupId={groupPromptsFilter}
                        groups={groups}
                        setShareButtonTarget={
                          (idx === 1 &&
                            setWorkgroupOwnerTeachingPopoverTarget) ||
                          undefined
                        }
                      />
                    </ConditionalWrap>
                  );
                })}
              </ConditionalWrap>
              {isShowHasMore && !showGroupPromptsOnly && hasMore && (
                <ConditionalWrap
                  condition={!enableCarousel}
                  wrapIfTrue={(children) => (
                    <Row className={styles.footerRow}>{children}</Row>
                  )}
                >
                  <ConditionalWrap
                    condition={!enableCarousel}
                    wrapIfTrue={(children) => (
                      <Column size="auto">{children}</Column>
                    )}
                  >
                    <Button
                      size="large"
                      disabled={isLoading}
                      onClick={() => {
                        handleCardFocus();
                        onMoreClicked();
                      }}
                      data-testid="show-more-button"
                      className={styles.moreButton}
                    >
                      {localizations.ShowMoreButtonLabel()}
                    </Button>
                  </ConditionalWrap>
                </ConditionalWrap>
              )}
            </>
          )}
          <Dialog
            open={dialogOpen}
            onOpenChange={(_event, data) => {
              if (!data.open) {
                handleDialogClose();
              }
              setDialogOpen(data.open);
            }}
          >
            {activePrompt ? (
              <PromptDetailModal
                prompt={activePrompt}
                morePrompts={morePrompts}
                onClicked={handleCardClick}
                onBookmarkToggled={onPromptBookmarkToggled}
                onShareClicked={onSharePromptClicked}
                onCopyClicked={onCopyPromptClicked}
                isBookmarkEnabled={isFlightActive("isBookmarksEnabled")}
                isPromptSavingEnabled={isFlightActive("isPromptSavingEnabled")}
                isPromptSharingEnabled={isFlightActive(
                  "isPromptSharingEnabled"
                )}
                isPromptSharingViaEmailEnabled={isFlightActive(
                  "isPromptSharingViaEmailEnabled"
                )}
                onDelete={(prompt) => {
                  setDialogOpen(false);
                  onDelete && onDelete(prompt);
                }}
                isShowPromptAuthor={showGroupPromptsOnly}
                onTryInAppBadgeAction={
                  (!showGroupPromptsOnly && onTryInAppBadgeAction) || undefined
                }
              />
            ) : (
              <></>
            )}
          </Dialog>
        </ConditionalWrap>
      </>
    );
  }
);

AllPrompts.displayName = "AllPrompts";
